import { ArrowLeftOutlined, RedoOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import {
  EditableSelect,
  SelectOption,
} from 'features/libra/components/EditableSelect'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  FieldValue,
  FieldValues,
  FlagNode,
  FlowchartNode,
  InputField,
  OntologyField,
  OutcomeNode,
  QuestionNode,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  FlowchartDetails,
  FlowchartResult,
  FlowchartSection as FlowchartSectionType,
  FlowchartState,
} from 'features/libra/sections/flowchartSection/flowchartTypes'
import pickBy from 'lodash/pickBy'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { FlowchartProvider, useFlowchart } from './FlowchartContext'
import { FlagNodeRenderer } from './nodes/FlagNodeRenderer'
import { OutcomeNodeRenderer } from './nodes/OutcomeNodeRenderer'
import { QuestionNodeRenderer } from './nodes/QuestionNodeRenderer'

const { Text, Title } = Typography

const BOOLEAN_OR_UNKNOWN_OPTIONS = [
  { value: 'true', label: 'True', color: 'green' },
  { value: 'false', label: 'False', color: 'red' },
  { value: 'unknown', label: 'Unknown', color: 'orange' },
] as const

const OUTCOME_COLOR_MAP = new Map<string, string>(
  BOOLEAN_OR_UNKNOWN_OPTIONS.map((o) => [o.value, o.color])
)

const FlowchartContent: FC<{ onStartOver?: () => void }> = ({
  onStartOver,
}) => {
  const {
    currentNode,
    currentNodeId,
    state,
    answerQuestion,
    acknowledgeFlag,
    goBack,
    reset,
    ontologyFields,
  } = useFlowchart()

  const flagNodes = useMemo(() => {
    return pickBy(
      ontologyFields,
      (node) => node.category === FieldCategory.FLAG
    ) as Record<string, FlagNode>
  }, [ontologyFields])

  const nodeContent = useMemo(() => {
    if (!currentNode) return null
    switch (currentNode.category) {
      case FieldCategory.QUESTION:
        return (
          <QuestionNodeRenderer
            node={currentNode as QuestionNode}
            onAnswer={answerQuestion}
          />
        )
      case FieldCategory.FLAG:
        return (
          <FlagNodeRenderer
            node={currentNode as FlagNode}
            onContinue={acknowledgeFlag}
          />
        )
      case FieldCategory.OUTCOME:
        return (
          <OutcomeNodeRenderer
            node={currentNode as OutcomeNode}
            collectedFlags={state.collectedFlags}
            flagNodes={flagNodes}
            onReset={onStartOver || reset}
          />
        )
    }
  }, [
    currentNode,
    state,
    flagNodes,
    acknowledgeFlag,
    answerQuestion,
    onStartOver,
    reset,
  ])

  if (!currentNode) {
    return (
      <ErrorContainer>
        <Text type='danger'>Unknown node: {currentNodeId}</Text>
      </ErrorContainer>
    )
  }
  return (
    <Container>
      {state.history.length > 0 && !state.isComplete && (
        <BackButton type='text' icon={<ArrowLeftOutlined />} onClick={goBack}>
          Back
        </BackButton>
      )}

      {/* Progress indicator */}
      {!state.isComplete && (
        <ProgressText type='secondary'>
          Question {state.history.length + 1}
          {state.collectedFlags.length > 0 &&
            ` • ${state.collectedFlags.length} flag${state.collectedFlags.length > 1 ? 's' : ''}`}
        </ProgressText>
      )}
      {nodeContent}
    </Container>
  )
}

interface CompletedFlowchartViewProps {
  title: string
  fieldDef: InputField
  value: unknown
  onChange: (value: unknown) => void
  onStartOver: () => void
}

const CompletedFlowchartView: FC<CompletedFlowchartViewProps> = ({
  title,
  fieldDef,
  value,
  onChange,
  onStartOver,
}) => {
  // Compute options for enum fields
  const options = useMemo<SelectOption<string>[] | null>(() => {
    if (fieldDef.type === 'enum' && fieldDef.allowedValues) {
      return fieldDef.allowedValues.map((val) => ({
        value: val,
        label: val.charAt(0).toUpperCase() + val.slice(1),
        color: OUTCOME_COLOR_MAP.get(val),
      }))
    }
    return null
  }, [fieldDef])

  return (
    <CompletedContainer>
      <Title level={4}>{title}</Title>

      <FieldContainer>
        <FieldLabel>{fieldDef.name}</FieldLabel>
        {options ? (
          <EditableSelect
            value={value as string | undefined}
            onChange={(newValue) => onChange(newValue)}
            options={options}
          />
        ) : (
          <Text>{String(value)}</Text>
        )}
      </FieldContainer>

      <StartOverButton
        icon={<RedoOutlined />}
        onClick={onStartOver}
        type='default'
        size='middle'>
        Start Over
      </StartOverButton>
    </CompletedContainer>
  )
}

interface FlowchartSectionProps {
  section: FlowchartSectionType
  fieldId: string
  fieldDef?: InputField
  ontologyFields: Record<string, OntologyField>
  fieldValue: FieldValue | undefined
  savedFieldValue: FieldValue | undefined
  detailsValue?: FieldValue | undefined
  setFieldValues: (
    updates: FieldValues | ((prev: FieldValues) => FieldValues)
  ) => void
}

export const FlowchartSectionView: FC<FlowchartSectionProps> = ({
  section,
  fieldId,
  fieldDef,
  ontologyFields,
  fieldValue,
  savedFieldValue,
  detailsValue,
  setFieldValues,
}) => {
  // Filter ontologyFields to only include flowchart nodes (not INPUT fields)
  const flowchartOntologyFields = useMemo(() => {
    return pickBy(
      ontologyFields,
      (field) =>
        field.category === FieldCategory.QUESTION ||
        field.category === FieldCategory.FLAG ||
        field.category === FieldCategory.OUTCOME
    ) as Record<string, FlowchartNode>
  }, [ontologyFields])

  // Unwrap all field values from { value, source } structure
  const unwrappedFieldValue = unwrapFieldValue(fieldValue)
  const unwrappedSavedFieldValue = unwrapFieldValue(savedFieldValue)
  const unwrappedDetails = unwrapFieldValue<FlowchartDetails>(detailsValue)
  const isInSync = unwrappedDetails?.result === unwrappedSavedFieldValue

  const handleComplete = useCallback(
    (result: FlowchartResult, state: FlowchartState) => {
      setFieldValues((prev) => ({
        ...prev,
        [fieldId]: wrapFieldValue(result.result),
        ...(section.detailsId && {
          [section.detailsId]: wrapFieldValue({
            result: result.result,
            flags: result.flags,
            answers: result.answers,
            path: [...state.history, state.currentNodeId],
          }),
        }),
      }))
    },
    [fieldId, section.detailsId, setFieldValues]
  )

  // Handle Manual Override
  const handleValueChange = useCallback(
    (value: unknown) => {
      setFieldValues((prev) => ({
        ...prev,
        [fieldId]: wrapFieldValue(value),
      }))
      // Note: This does NOT update detailsId, breaking sync intentionally
    },
    [fieldId, setFieldValues]
  )

  const handleStartOver = useCallback(() => {
    const keysToRemove = new Set([fieldId, section.detailsId].filter(Boolean))

    setFieldValues((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => !keysToRemove.has(key))
      )
    )
  }, [fieldId, section.detailsId, setFieldValues])

  const hasUnsavedChanges = unwrappedFieldValue !== unwrappedSavedFieldValue

  const shouldShowFlowchart =
    unwrappedFieldValue === undefined ||
    unwrappedFieldValue === null ||
    hasUnsavedChanges

  if (!shouldShowFlowchart) {
    if (section.detailsId && isInSync && unwrappedDetails) {
      const initialState: FlowchartState = {
        currentNodeId: unwrappedDetails.path[unwrappedDetails.path.length - 1],
        answers: unwrappedDetails.answers,
        history: unwrappedDetails.path.slice(0, -1),
        collectedFlags: unwrappedDetails.flags,
        isComplete: true,
        result: unwrappedDetails.result,
      }

      return (
        <FlowchartProvider
          key='completed-with-details'
          section={section}
          ontologyFields={flowchartOntologyFields}
          initialState={initialState}
          onComplete={handleComplete}>
          <FlowchartContent onStartOver={handleStartOver} />
        </FlowchartProvider>
      )
    }

    // Otherwise show simple completed view (select dropdown only)
    if (!fieldDef) {
      return (
        <ErrorContainer>
          <Text type='warning'>
            Configuration issue: No field definition for &quot;{fieldId}&quot;
          </Text>
          <Text>Current value: {String(unwrappedFieldValue)}</Text>
          <Button onClick={handleStartOver}>Start Over</Button>
        </ErrorContainer>
      )
    }

    return (
      <CompletedFlowchartView
        title={section.title || fieldDef.name}
        fieldDef={fieldDef}
        value={unwrappedFieldValue}
        onChange={handleValueChange}
        onStartOver={handleStartOver}
      />
    )
  }

  return (
    <FlowchartProvider
      key='active-flowchart'
      section={section}
      ontologyFields={flowchartOntologyFields}
      onComplete={handleComplete}>
      <FlowchartContent />
    </FlowchartProvider>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
`

const BackButton = styled(Button)`
  align-self: flex-start;
  padding-left: 0;
`

const ProgressText = styled(Text)`
  text-align: center;
  font-size: 12px;
`

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
`

const CompletedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
`

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FieldLabel = styled(Text)`
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StartOverButton = styled(Button)`
  align-self: flex-start;
`
